<script setup>
import { computed, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'

import Avatar from '@/components/Avatar.vue'
import Button from '@/components/Button.vue'
import { uploadBotAvatar, removeBotAvatar } from '@/api/inboxes'
import NextInput from '@/components/Form/NextInput.vue'
import { useAlert } from '@/composables/useToast'

const store = useStore()
const route = useRoute()
const { t } = useI18n()

const inboxId = Number(route.params.inboxId)

const activeTab = ref('settings')
const name = ref('')
const lockToSingleConversation = ref(false)
const askTagOnResolve = ref(false)
const clientSurveys = ref(false)
const virtualOperator = ref(false)
const virtualOperatorShare = ref(100)
const botName = ref('')
const botAvatarUrl = ref(null)
const botAvatarInput = ref(null)
const botAvatarBusy = ref(false)
const SHARE_STEPS = Array.from({ length: 11 }, (_, index) => index * 10)
const autoResolveHours = ref('')
const selectedMemberIds = ref([])
const portionSize = ref('')
const requeueAllOnOffline = ref(true)
const isLoaded = ref(false)

// Monday-first display order; keys are wday numbers as the backend stores them
const DAY_ORDER = ['1', '2', '3', '4', '5', '6', '0']
const schedule = ref({})
const outOfOfficeMessage = ref('')
const welcomeMessage = ref('')
const inactivityTimeout = ref('')
const intakeCloses = ref(0)

const widgetColor = ref('#1f93ff')
const welcomeTitle = ref('')
const welcomeTagline = ref('')

const vkConfirmationToken = ref('')
const vkSecretKey = ref('')

const inbox = computed(() => store.getters['inboxes/getInboxById'](inboxId))
const uiFlags = computed(() => store.getters['inboxes/getUIFlags'])
const agentList = computed(() => store.getters['agents/getAgents'])

const autoAssignmentEnabled = computed(
  () => inbox.value?.enable_auto_assignment || false
)

const syncFromInbox = record => {
  if (!record) return
  name.value = record.name
  lockToSingleConversation.value = record.lock_to_single_conversation
  askTagOnResolve.value = record.ask_tag_on_resolve
  clientSurveys.value = record.client_surveys
  virtualOperator.value = record.virtual_operator
  virtualOperatorShare.value = record.virtual_operator_share ?? 100
  botName.value = record.bot_name || ''
  botAvatarUrl.value = record.bot_avatar_url || null
  autoResolveHours.value = record.auto_resolve_after_hours ?? ''
  selectedMemberIds.value = [...(record.member_ids || [])]
  portionSize.value = record.auto_assignment_config?.portion_size ?? ''
  requeueAllOnOffline.value =
    record.auto_assignment_config?.requeue_all_on_offline !== false
  const hours = record.working_hours || {}
  const nextSchedule = {}
  DAY_ORDER.forEach(wday => {
    const window = hours[wday]
    nextSchedule[wday] = {
      enabled: Boolean(window?.open && window?.close),
      open: window?.open || '09:00',
      close: window?.close || '18:00',
    }
  })
  schedule.value = nextSchedule
  outOfOfficeMessage.value = record.out_of_office_message || ''
  welcomeMessage.value = record.welcome_message || ''
  inactivityTimeout.value = record.inactivity_timeout_minutes ?? ''
  intakeCloses.value = record.intake_closes_minutes_before ?? 0
  widgetColor.value = record.channel?.widget_color || '#1f93ff'
  welcomeTitle.value = record.channel?.welcome_title || ''
  welcomeTagline.value = record.channel?.welcome_tagline || ''
  vkConfirmationToken.value = record.channel?.confirmation_token || ''
  vkSecretKey.value = record.channel?.secret_key || ''
  isLoaded.value = true
}

const isWebWidget = computed(() => inbox.value?.channel_slug === 'web_widget')
const isVk = computed(() => inbox.value?.channel_slug === 'vk')

// VK calls this address to confirm the server and to deliver events
const vkWebhookUrl = computed(() => inbox.value?.channel?.webhook_url || '')

const copyVkWebhookUrl = async () => {
  await navigator.clipboard.writeText(vkWebhookUrl.value)
  useAlert(t('INBOX_MGMT.SETTINGS.VK.WEBHOOK.COPIED'))
}

const embedCode = computed(() => {
  const token = inbox.value?.channel?.website_token
  const scriptClose = '<' + '/script>'
  return [
    '<script>',
    `  window.mkChat = { websiteToken: "${token}" };`,
    scriptClose,
    `<script src="${window.location.origin}/sdk.js" async>${scriptClose}`,
  ].join('\n')
})

const copyEmbedCode = async () => {
  await navigator.clipboard.writeText(embedCode.value)
  useAlert(t('INBOX_MGMT.SETTINGS.WIDGET.EMBED.COPIED'))
}

const buildWorkingHours = () => {
  const result = {}
  DAY_ORDER.forEach(wday => {
    const day = schedule.value[wday]
    if (day?.enabled && day.open && day.close) {
      result[wday] = { open: day.open, close: day.close }
    }
  })
  return result
}

const updateAssignmentLimit = async () => {
  try {
    await store.dispatch('inboxes/update', {
      id: inboxId,
      auto_assignment_config: {
        portion_size:
          portionSize.value === '' || portionSize.value === null
            ? null
            : Number(portionSize.value),
        requeue_all_on_offline: requeueAllOnOffline.value,
      },
    })
    useAlert(t('INBOX_MGMT.COLLABORATORS.API.SUCCESS'))
  } catch (error) {
    useAlert(t('INBOX_MGMT.COLLABORATORS.API.ERROR'))
  }
}

const toggleMember = agentId => {
  if (selectedMemberIds.value.includes(agentId)) {
    selectedMemberIds.value = selectedMemberIds.value.filter(
      id => id !== agentId
    )
  } else {
    selectedMemberIds.value = [...selectedMemberIds.value, agentId]
  }
}

const updateSettings = async () => {
  try {
    await store.dispatch('inboxes/update', {
      id: inboxId,
      name: name.value,
      lock_to_single_conversation: lockToSingleConversation.value,
      ask_tag_on_resolve: askTagOnResolve.value,
      client_surveys: clientSurveys.value,
      virtual_operator: virtualOperator.value,
      virtual_operator_share: Number(virtualOperatorShare.value),
      bot_name: botName.value.trim(),
      auto_resolve_after_hours:
        autoResolveHours.value === '' ? null : Number(autoResolveHours.value),
      working_hours: buildWorkingHours(),
      out_of_office_message: outOfOfficeMessage.value,
      welcome_message: welcomeMessage.value,
      inactivity_timeout_minutes:
        inactivityTimeout.value === '' ? null : Number(inactivityTimeout.value),
      intake_closes_minutes_before: Number(intakeCloses.value) || 0,
      ...(isWebWidget.value
        ? {
            channel: {
              widget_color: widgetColor.value,
              welcome_title: welcomeTitle.value,
              welcome_tagline: welcomeTagline.value,
            },
          }
        : {}),
      ...(isVk.value
        ? {
            channel: {
              confirmation_token: vkConfirmationToken.value,
              secret_key: vkSecretKey.value,
            },
          }
        : {}),
    })
    useAlert(t('INBOX_MGMT.SETTINGS.API.SUCCESS'))
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS.API.ERROR'))
  }
}

const accountIdForAvatar = () =>
  store.getters['auth/getCurrentUser']?.accounts?.[0]?.id

const uploadAvatar = async event => {
  const file = event.target.files?.[0]
  if (!file) return
  botAvatarBusy.value = true
  try {
    const { data } = await uploadBotAvatar(accountIdForAvatar(), inboxId, file)
    botAvatarUrl.value = data.bot_avatar_url
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_AVATAR.ERROR'))
  } finally {
    botAvatarBusy.value = false
    event.target.value = ''
  }
}

const removeAvatar = async () => {
  botAvatarBusy.value = true
  try {
    await removeBotAvatar(accountIdForAvatar(), inboxId)
    botAvatarUrl.value = null
  } catch (error) {
    useAlert(t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_AVATAR.ERROR'))
  } finally {
    botAvatarBusy.value = false
  }
}

const updateMembers = async () => {
  try {
    await store.dispatch('inboxes/update', {
      id: inboxId,
      member_ids: selectedMemberIds.value,
    })
    useAlert(t('INBOX_MGMT.COLLABORATORS.API.SUCCESS'))
  } catch (error) {
    useAlert(t('INBOX_MGMT.COLLABORATORS.API.ERROR'))
  }
}

const toggleAutoAssignment = async () => {
  try {
    await store.dispatch('inboxes/update', {
      id: inboxId,
      enable_auto_assignment: !autoAssignmentEnabled.value,
    })
    useAlert(t('INBOX_MGMT.COLLABORATORS.API.SUCCESS'))
  } catch (error) {
    useAlert(t('INBOX_MGMT.COLLABORATORS.API.ERROR'))
  }
}

onMounted(async () => {
  store.dispatch('agents/get')
  const record = await store.dispatch('inboxes/show', inboxId)
  syncFromInbox(record)
})
</script>

<template>
  <div
    class="flex flex-col w-full h-full px-6 pt-4 pb-8 m-0 overflow-auto bg-n-surface-1"
  >
    <div class="flex items-start w-full mx-auto max-w-5xl">
      <div v-if="isLoaded" class="flex flex-col w-full h-full gap-4 font-inter">
        <div class="flex flex-col gap-2">
          <router-link
            :to="{ name: 'settings_inboxes' }"
            class="flex items-center gap-1 text-body-main text-n-slate-11 hover:text-n-slate-12"
          >
            <span class="i-lucide-chevron-left size-4" />
            {{ $t('INBOX_MGMT.HEADER') }}
          </router-link>
          <h1 class="text-heading-1 text-n-slate-12">
            {{ inbox?.name }}
          </h1>
        </div>

        <!-- Tab bar -->
        <div class="flex items-center gap-4 border-b border-n-weak">
          <button
            type="button"
            class="px-1 pb-2 -mb-px text-sm font-medium border-b-2"
            :class="
              activeTab === 'settings'
                ? 'border-n-brand text-n-slate-12'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = 'settings'"
          >
            {{ $t('INBOX_MGMT.TABS.SETTINGS') }}
          </button>
          <button
            type="button"
            class="px-1 pb-2 -mb-px text-sm font-medium border-b-2"
            :class="
              activeTab === 'collaborators'
                ? 'border-n-brand text-n-slate-12'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeTab = 'collaborators'"
          >
            {{ $t('INBOX_MGMT.TABS.COLLABORATORS') }}
          </button>
        </div>

        <!-- Settings tab -->
        <div v-if="activeTab === 'settings'" class="flex flex-col gap-6 max-w-3xl">
          <NextInput
            v-model="name"
            name="inbox_name"
            type="text"
            :label="$t('INBOX_MGMT.SETTINGS.NAME.LABEL')"
            :placeholder="$t('INBOX_MGMT.SETTINGS.NAME.PLACEHOLDER')"
          />

          <div class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS.ROUTING.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.ROUTING.DESC') }}
            </p>
            <div class="flex flex-col sm:flex-row items-start gap-4 mt-3 min-w-0">
              <label
                for="routing_new"
                class="rounded-xl outline outline-1 p-4 transition-all duration-200 bg-n-solid-1 py-4 pl-4 pr-6 cursor-pointer flex-1"
                :class="
                  !lockToSingleConversation
                    ? 'outline-n-blue-9'
                    : 'outline-n-weak hover:outline-n-strong'
                "
              >
                <div class="flex flex-col gap-2 items-start">
                  <div class="flex items-center justify-between w-full gap-3">
                    <h3 class="text-heading-3 text-n-slate-12">
                      {{ $t('INBOX_MGMT.SETTINGS.ROUTING.NEW.TITLE') }}
                    </h3>
                    <input
                      id="routing_new"
                      type="radio"
                      name="conversation_routing"
                      :checked="!lockToSingleConversation"
                      class="shadow cursor-pointer grid place-items-center border-2 border-n-strong appearance-none rounded-full w-5 h-5 checked:bg-n-brand before:content-[''] before:bg-n-brand before:border-4 before:rounded-full before:border-n-strong checked:before:w-[18px] checked:before:h-[18px] checked:border checked:border-n-brand"
                      @change="lockToSingleConversation = false"
                    />
                  </div>
                  <p class="text-body-main text-n-slate-11">
                    {{ $t('INBOX_MGMT.SETTINGS.ROUTING.NEW.DESC') }}
                  </p>
                </div>
              </label>
              <label
                for="routing_reopen"
                class="rounded-xl outline outline-1 p-4 transition-all duration-200 bg-n-solid-1 py-4 pl-4 pr-6 cursor-pointer flex-1"
                :class="
                  lockToSingleConversation
                    ? 'outline-n-blue-9'
                    : 'outline-n-weak hover:outline-n-strong'
                "
              >
                <div class="flex flex-col gap-2 items-start">
                  <div class="flex items-center justify-between w-full gap-3">
                    <h3 class="text-heading-3 text-n-slate-12">
                      {{ $t('INBOX_MGMT.SETTINGS.ROUTING.REOPEN.TITLE') }}
                    </h3>
                    <input
                      id="routing_reopen"
                      type="radio"
                      name="conversation_routing"
                      :checked="lockToSingleConversation"
                      class="shadow cursor-pointer grid place-items-center border-2 border-n-strong appearance-none rounded-full w-5 h-5 checked:bg-n-brand before:content-[''] before:bg-n-brand before:border-4 before:rounded-full before:border-n-strong checked:before:w-[18px] checked:before:h-[18px] checked:border checked:border-n-brand"
                      @change="lockToSingleConversation = true"
                    />
                  </div>
                  <p class="text-body-main text-n-slate-11">
                    {{ $t('INBOX_MGMT.SETTINGS.ROUTING.REOPEN.DESC') }}
                  </p>
                </div>
              </label>
            </div>
          </div>

          <div class="flex flex-col gap-1.5 max-w-xs">
            <NextInput
              v-model="autoResolveHours"
              name="auto_resolve_hours"
              type="number"
              min="1"
              :label="$t('INBOX_MGMT.SETTINGS.AUTO_RESOLVE.LABEL')"
              :placeholder="$t('INBOX_MGMT.SETTINGS.AUTO_RESOLVE.PLACEHOLDER')"
            />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.AUTO_RESOLVE.SUB_TEXT') }}
            </p>
          </div>

          <template v-if="isWebWidget">
            <div class="flex flex-col gap-1.5">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ $t('INBOX_MGMT.SETTINGS.WIDGET.EMBED.TITLE') }}
              </h3>
              <p class="text-body-main text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS.WIDGET.EMBED.DESC') }}
              </p>
              <textarea
                :value="embedCode"
                readonly
                rows="4"
                class="w-full px-3 py-2 font-mono text-xs border-none rounded-xl resize-none outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <div>
                <Button
                  sm
                  faded
                  slate
                  icon="i-lucide-copy"
                  :label="$t('INBOX_MGMT.SETTINGS.WIDGET.EMBED.COPY')"
                  @click="copyEmbedCode"
                />
              </div>
            </div>

            <div class="flex flex-col gap-1.5 max-w-xs">
              <label
                for="widget_color"
                class="text-heading-3 text-n-slate-12"
              >
                {{ $t('INBOX_MGMT.SETTINGS.WIDGET.COLOR.LABEL') }}
              </label>
              <input
                id="widget_color"
                v-model="widgetColor"
                type="color"
                class="h-10 w-20 p-1 rounded-lg border border-n-weak bg-n-alpha-black2 cursor-pointer"
              />
            </div>

            <NextInput
              v-model="welcomeTitle"
              name="widget_welcome_title"
              type="text"
              :label="$t('INBOX_MGMT.SETTINGS.WIDGET.TITLE.LABEL')"
              :placeholder="$t('INBOX_MGMT.SETTINGS.WIDGET.TITLE.PLACEHOLDER')"
            />
            <NextInput
              v-model="welcomeTagline"
              name="widget_welcome_tagline"
              type="text"
              :label="$t('INBOX_MGMT.SETTINGS.WIDGET.TAGLINE.LABEL')"
              :placeholder="
                $t('INBOX_MGMT.SETTINGS.WIDGET.TAGLINE.PLACEHOLDER')
              "
            />
          </template>

          <template v-if="isVk">
            <div class="flex flex-col gap-1.5">
              <h3 class="text-heading-3 text-n-slate-12">
                {{ $t('INBOX_MGMT.SETTINGS.VK.WEBHOOK.TITLE') }}
              </h3>
              <p class="text-body-main text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS.VK.WEBHOOK.DESC') }}
              </p>
              <textarea
                :value="vkWebhookUrl"
                readonly
                rows="2"
                class="w-full px-3 py-2 font-mono text-xs border-none rounded-xl resize-none outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12"
              />
              <div>
                <Button
                  sm
                  faded
                  slate
                  icon="i-lucide-copy"
                  :label="$t('INBOX_MGMT.SETTINGS.VK.WEBHOOK.COPY')"
                  @click="copyVkWebhookUrl"
                />
              </div>
            </div>

            <NextInput
              v-model="vkConfirmationToken"
              name="vk_confirmation_token"
              type="text"
              :label="$t('INBOX_MGMT.SETTINGS.VK.CONFIRMATION.LABEL')"
              :placeholder="$t('INBOX_MGMT.SETTINGS.VK.CONFIRMATION.PLACEHOLDER')"
            />
            <p class="text-body-main text-n-slate-11 -mt-4">
              {{ $t('INBOX_MGMT.SETTINGS.VK.CONFIRMATION.SUB_TEXT') }}
            </p>

            <NextInput
              v-model="vkSecretKey"
              name="vk_secret_key"
              type="text"
              :label="$t('INBOX_MGMT.SETTINGS.VK.SECRET.LABEL')"
              :placeholder="$t('INBOX_MGMT.SETTINGS.VK.SECRET.PLACEHOLDER')"
            />
          </template>

          <div class="flex flex-col gap-1.5">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS.WORKING_HOURS.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.WORKING_HOURS.DESC') }}
            </p>
            <div class="flex flex-col gap-1.5 mt-2">
              <div
                v-for="wday in DAY_ORDER"
                :key="wday"
                class="flex items-center gap-3"
              >
                <label
                  class="flex items-center gap-2 w-32 text-sm text-n-slate-12 cursor-pointer"
                >
                  <input
                    v-model="schedule[wday].enabled"
                    type="checkbox"
                    class="w-4 h-4 accent-n-brand"
                  />
                  {{ $t(`INBOX_MGMT.SETTINGS.WORKING_HOURS.DAYS.${wday}`) }}
                </label>
                <input
                  v-model="schedule[wday].open"
                  type="time"
                  :disabled="!schedule[wday].enabled"
                  class="h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand disabled:opacity-40"
                />
                <span class="text-n-slate-10">—</span>
                <input
                  v-model="schedule[wday].close"
                  type="time"
                  :disabled="!schedule[wday].enabled"
                  class="h-8 px-2 text-sm border-none rounded-lg outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 focus:outline-n-brand disabled:opacity-40"
                />
              </div>
            </div>
          </div>

          <div class="flex flex-col gap-1.5">
            <label
              for="out_of_office_message"
              class="text-heading-3 text-n-slate-12"
            >
              {{ $t('INBOX_MGMT.SETTINGS.OUT_OF_OFFICE.LABEL') }}
            </label>
            <textarea
              id="out_of_office_message"
              v-model="outOfOfficeMessage"
              rows="3"
              :placeholder="$t('INBOX_MGMT.SETTINGS.OUT_OF_OFFICE.PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm border-none rounded-xl resize-none outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.OUT_OF_OFFICE.SUB_TEXT') }}
            </p>
          </div>

          <div v-if="inbox?.welcome_possible" class="flex flex-col gap-1.5">
            <label for="welcome_message" class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS.WELCOME.LABEL') }}
            </label>
            <textarea
              id="welcome_message"
              v-model="welcomeMessage"
              rows="3"
              :placeholder="$t('INBOX_MGMT.SETTINGS.WELCOME.PLACEHOLDER')"
              class="w-full px-3 py-2 text-sm border-none rounded-xl resize-none outline outline-1 outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.WELCOME.SUB_TEXT') }}
            </p>
          </div>

          <div class="flex flex-col gap-1.5 max-w-xs">
            <NextInput
              v-model="inactivityTimeout"
              name="inactivity_timeout"
              type="number"
              min="0"
              :label="$t('INBOX_MGMT.SETTINGS.INACTIVITY.LABEL')"
              :placeholder="$t('INBOX_MGMT.SETTINGS.INACTIVITY.PLACEHOLDER')"
            />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.INACTIVITY.SUB_TEXT') }}
            </p>
          </div>

          <div class="flex flex-col gap-1.5 max-w-xs">
            <NextInput
              v-model="intakeCloses"
              name="intake_closes_minutes_before"
              type="number"
              min="0"
              :label="$t('INBOX_MGMT.SETTINGS.INTAKE_CLOSE.LABEL')"
              :placeholder="$t('INBOX_MGMT.SETTINGS.INTAKE_CLOSE.PLACEHOLDER')"
            />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.INTAKE_CLOSE.SUB_TEXT', { bot: botName || $t('CONVERSATIONS.BOT_NAME') }) }}
            </p>
          </div>

          <label class="flex items-start gap-2 cursor-pointer max-w-xs">
            <input
              v-model="askTagOnResolve"
              type="checkbox"
              class="w-4 h-4 mt-0.5 accent-n-brand"
            />
            <span class="flex flex-col gap-1">
              <span class="text-heading-3 text-n-slate-12">
                {{ $t('INBOX_MGMT.SETTINGS.ASK_TAG.LABEL') }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS.ASK_TAG.SUB_TEXT') }}
              </span>
            </span>
          </label>

          <label
            v-if="inbox?.client_surveys_possible"
            class="flex items-start gap-2 cursor-pointer max-w-xs"
          >
            <input
              v-model="clientSurveys"
              type="checkbox"
              class="w-4 h-4 mt-0.5 accent-n-brand"
            />
            <span class="flex flex-col gap-1">
              <span class="text-heading-3 text-n-slate-12">
                {{ $t('INBOX_MGMT.SETTINGS.CLIENT_SURVEYS.LABEL') }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS.CLIENT_SURVEYS.SUB_TEXT', { bot: botName || $t('CONVERSATIONS.BOT_NAME') }) }}
              </span>
            </span>
          </label>

          <label class="flex items-start gap-2 cursor-pointer max-w-xs">
            <input
              v-model="virtualOperator"
              type="checkbox"
              class="w-4 h-4 mt-0.5 accent-n-brand"
            />
            <span class="flex flex-col gap-1">
              <span class="text-heading-3 text-n-slate-12">
                {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.LABEL', { bot: botName || $t('CONVERSATIONS.BOT_NAME') }) }}
              </span>
              <span class="text-body-main text-n-slate-11">
                {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.SUB_TEXT', { bot: botName || $t('CONVERSATIONS.BOT_NAME') }) }}
              </span>
            </span>
          </label>

          <div v-if="virtualOperator" class="flex flex-col gap-1.5 max-w-xs">
            <label
              class="text-heading-3 text-n-slate-12"
              for="virtual_operator_share"
            >
              {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.SHARE.LABEL') }}
            </label>
            <select
              id="virtual_operator_share"
              v-model="virtualOperatorShare"
              class="w-full px-3 h-10 rounded-lg border border-n-weak bg-n-alpha-black2 text-body-main text-n-slate-12"
            >
              <option v-for="step in SHARE_STEPS" :key="step" :value="step">
                {{
                  $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.SHARE.OPTION', {
                    value: step,
                  })
                }}
              </option>
            </select>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.SHARE.SUB_TEXT') }}
            </p>
          </div>

          <div v-if="virtualOperator" class="flex flex-col gap-1.5 max-w-xs">
            <label class="text-heading-3 text-n-slate-12" for="bot_name">
              {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_NAME.LABEL') }}
            </label>
            <input
              id="bot_name"
              v-model="botName"
              type="text"
              maxlength="40"
              class="w-full px-3 h-10 rounded-lg border border-n-weak bg-n-alpha-black2 text-body-main text-n-slate-12"
            />
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_NAME.HINT') }}
            </p>
          </div>

          <div v-if="virtualOperator" class="flex flex-col gap-1.5">
            <span class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_AVATAR.LABEL') }}
            </span>
            <div class="flex items-center gap-3">
              <img
                :src="botAvatarUrl || '/widget/avatar-bot.png'"
                alt=""
                class="object-cover rounded-full size-12 bg-n-alpha-1"
              />
              <input
                ref="botAvatarInput"
                type="file"
                accept="image/*"
                class="hidden"
                @change="uploadAvatar"
              />
              <Button
                sm
                faded
                slate
                type="button"
                :label="$t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_AVATAR.UPLOAD')"
                :is-loading="botAvatarBusy"
                @click="botAvatarInput?.click()"
              />
              <Button
                v-if="botAvatarUrl"
                sm
                ghost
                slate
                type="button"
                :label="$t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_AVATAR.REMOVE')"
                :disabled="botAvatarBusy"
                @click="removeAvatar"
              />
            </div>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.SETTINGS.VIRTUAL_OPERATOR.BOT_AVATAR.HINT') }}
            </p>
          </div>

          <div>
            <Button
              :label="$t('INBOX_MGMT.SETTINGS.UPDATE')"
              :disabled="uiFlags.isUpdating"
              @click="updateSettings"
            />
          </div>
        </div>

        <!-- Collaborators tab -->
        <div
          v-if="activeTab === 'collaborators'"
          class="flex flex-col gap-6 max-w-3xl"
        >
          <div class="flex flex-col gap-1">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.COLLABORATORS.TITLE') }}
            </h3>
            <p class="text-body-main text-n-slate-11">
              {{ $t('INBOX_MGMT.COLLABORATORS.DESC') }}
            </p>
            <div class="flex flex-col gap-2 mt-3">
              <label
                v-for="agent in agentList"
                :key="agent.id"
                class="flex items-center gap-3 p-3 border rounded-xl border-n-weak cursor-pointer hover:bg-n-alpha-2"
              >
                <input
                  type="checkbox"
                  class="w-4 h-4 accent-n-brand"
                  :checked="selectedMemberIds.includes(agent.id)"
                  @change="toggleMember(agent.id)"
                />
                <Avatar
                  :name="agent.name"
                  :size="28"
                  :status="agent.availability || 'offline'"
                  :src="agent.avatar_url"
                  logo-fallback
                />
                <span class="text-body-main text-n-slate-12">
                  {{ agent.name }}
                </span>
              </label>
            </div>
          </div>

          <div>
            <Button
              :label="$t('INBOX_MGMT.SETTINGS.UPDATE')"
              :disabled="uiFlags.isUpdating"
              @click="updateMembers"
            />
          </div>

          <div class="flex flex-col gap-1 pt-4 border-t border-n-weak">
            <h3 class="text-heading-3 text-n-slate-12">
              {{ $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.TITLE') }}
            </h3>
            <div
              class="flex items-start justify-between gap-4 p-4 mt-3 rounded-xl outline outline-1 outline-n-weak bg-n-solid-1"
            >
              <div class="flex flex-col gap-2">
                <h3 class="text-heading-3 text-n-slate-12">
                  {{ $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.STANDARD.TITLE') }}
                </h3>
                <p class="text-body-main text-n-slate-11">
                  {{ $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.STANDARD.DESC') }}
                </p>
                <ul class="m-0 pl-5 list-disc text-body-main text-n-slate-11">
                  <li>
                    {{
                      $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.STANDARD.OLDEST')
                    }}
                  </li>
                  <li>
                    {{
                      $t(
                        'INBOX_MGMT.COLLABORATORS.ASSIGNMENT.STANDARD.ROUND_ROBIN'
                      )
                    }}
                  </li>
                </ul>

                <div
                  v-if="autoAssignmentEnabled"
                  class="flex flex-col w-full gap-1.5 pt-3 mt-1 border-t border-n-weak"
                >
                  <NextInput
                    v-model="portionSize"
                    name="portion_size"
                    type="number"
                    min="1"
                    :label="
                      $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.PORTION.LABEL')
                    "
                    :placeholder="
                      $t(
                        'INBOX_MGMT.COLLABORATORS.ASSIGNMENT.PORTION.PLACEHOLDER'
                      )
                    "
                  />
                  <p class="text-body-main text-n-slate-11">
                    {{
                      $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.PORTION.SUB_TEXT')
                    }}
                  </p>
                  <label
                    class="flex items-start gap-2 mt-2 text-body-main text-n-slate-12 cursor-pointer"
                  >
                    <input
                      v-model="requeueAllOnOffline"
                      type="checkbox"
                      class="mt-0.5 w-4 h-4 accent-n-brand"
                    />
                    <span>
                      {{
                        $t('INBOX_MGMT.COLLABORATORS.ASSIGNMENT.REQUEUE_ALL.LABEL')
                      }}
                      <span class="block text-n-slate-11">
                        {{
                          $t(
                            'INBOX_MGMT.COLLABORATORS.ASSIGNMENT.REQUEUE_ALL.SUB_TEXT'
                          )
                        }}
                      </span>
                    </span>
                  </label>
                  <div class="mt-1">
                    <Button
                      sm
                      :label="$t('INBOX_MGMT.SETTINGS.UPDATE')"
                      :disabled="uiFlags.isUpdating"
                      @click="updateAssignmentLimit"
                    />
                  </div>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                :aria-checked="autoAssignmentEnabled"
                class="relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors"
                :class="autoAssignmentEnabled ? 'bg-n-brand' : 'bg-n-slate-6'"
                @click="toggleAutoAssignment"
              >
                <span
                  class="inline-block h-4 w-4 rounded-full bg-white transition-transform"
                  :class="
                    autoAssignmentEnabled
                      ? 'translate-x-[18px]'
                      : 'translate-x-0.5'
                  "
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
